/**
 * Support settings — where missed chats go, the auto-reply, and business hours.
 *
 * Superadmin only (the route guard and the API both enforce). The support email is the
 * address a conversation is escalated to outside business hours; the auto-reply is the
 * system message a customer gets when they open a thread; business hours drive whether the
 * app tells them an agent is online. Saving sends a full settings patch to the typed
 * client and works offline against the mock.
 */
import { useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import type { BusinessHours, SupportSettings } from '../contracts/types.js';
import { Button, Card, ErrorBox, Field, Input, LoadingState, Switch, Textarea } from '../components/ui.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';

const WEEKDAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
] as const;

type Weekday = (typeof WEEKDAYS)[number]['key'];
type DayState = { enabled: boolean; open: string; close: string };

export function SupportSettingsPage(): JSX.Element {
  const { api } = useAuth();
  const state = useAsync(() => api.getSettings(), []);

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Support settings</h1>
        <p className="page-head__sub">
          The address missed chats escalate to, the auto-reply customers receive, and the hours an agent
          is shown as available.
        </p>
      </div>

      {state.loading ? (
        <LoadingState label="Loading settings…" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : state.data === null ? (
        <ErrorBox message="Settings are not available." />
      ) : (
        <SettingsForm initial={state.data} onSaved={(next) => state.setData(() => next)} />
      )}
    </>
  );
}

function SettingsForm({
  initial,
  onSaved,
}: {
  initial: SupportSettings;
  onSaved: (next: SupportSettings) => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();

  const [supportEmail, setSupportEmail] = useState(initial.supportEmail);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(initial.autoReplyEnabled);
  const [autoReplyMessage, setAutoReplyMessage] = useState(initial.autoReplyMessage);
  const [timezone, setTimezone] = useState(initial.businessHours.timezone);
  const [days, setDays] = useState<Record<Weekday, DayState>>(() => initialDays(initial.businessHours));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function setDay(key: Weekday, patch: Partial<DayState>) {
    setDays((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  async function save() {
    setFormError(null);
    const email = supportEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setFormError('Enter a valid support email address.');
      return;
    }
    for (const { key, label } of WEEKDAYS) {
      const d = days[key];
      if (d.enabled && d.open >= d.close) {
        setFormError(`${label}: closing time must be after opening time.`);
        return;
      }
    }

    const payload: SupportSettings = {
      supportEmail: email,
      autoReplyEnabled,
      autoReplyMessage: autoReplyMessage.trim(),
      businessHours: {
        timezone: timezone.trim() || 'Africa/Lagos',
        days: WEEKDAYS.filter(({ key }) => days[key].enabled).map(({ key }) => ({
          day: key,
          open: days[key].open,
          close: days[key].close,
        })),
      },
    };

    setSaving(true);
    try {
      const next = await api.setSettings(payload);
      onSaved(next);
      toast.success('Support settings saved');
    } catch (err) {
      toast.error('Could not save', messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid--2">
      <Card title="Contact & auto-reply">
        <Field label="Support email (where missed chats go)" htmlFor="support-email">
          <p className="muted setting-help">
            Where a conversation is emailed when nobody answers it in the console before
            business hours end. It is not shown to customers and is not a reply-to address:
            a customer replying to that mail reaches this inbox, not the chat thread.
          </p>
          <Input
            id="support-email"
            type="email"
            value={supportEmail}
            placeholder="support@genex.pay"
            onChange={(e) => setSupportEmail(e.target.value)}
          />
        </Field>

        <div className="setting-row">
          <div>
            <div style={{ fontWeight: 600 }}>Auto-reply</div>
            <div className="muted" style={{ fontSize: 13 }}>
              Send an automatic first message when a customer opens a conversation.
            </div>
          </div>
          <Switch
            checked={autoReplyEnabled}
            onChange={setAutoReplyEnabled}
            label="Toggle auto-reply"
          />
        </div>

        <Field label="Auto-reply message">
          <p className="muted setting-help">
            Sent once, immediately, as the first message in a new conversation. It is the only
            message a customer is guaranteed to get outside business hours, so it is worth
            saying when somebody will actually reply rather than only that they will.
          </p>
          <Textarea
            value={autoReplyMessage}
            rows={4}
            maxLength={1000}
            disabled={!autoReplyEnabled}
            placeholder="Thanks for reaching out. An agent will be with you shortly."
            onChange={(e) => setAutoReplyMessage(e.target.value)}
          />
        </Field>
      </Card>

      <Card title="Business hours">
        <Field label="Timezone" htmlFor="tz">
          <p className="muted setting-help">
            The zone the hours below are read in. An IANA name such as{' '}
            <code>Africa/Lagos</code>. Everything a customer sees about availability is
            computed from this, so a wrong zone shows the wrong open hours to everybody rather
            than failing visibly.
          </p>
          <Input id="tz" value={timezone} placeholder="Africa/Lagos" onChange={(e) => setTimezone(e.target.value)} />
        </Field>

        <div className="hours">
          {WEEKDAYS.map(({ key, label }) => {
            const d = days[key];
            return (
              <div key={key} className={`hours__row${d.enabled ? '' : ' hours__row--off'}`}>
                <label className="hours__day">
                  <Switch checked={d.enabled} onChange={(v) => setDay(key, { enabled: v })} label={label} />
                  <span>{label}</span>
                </label>
                {d.enabled ? (
                  <div className="hours__times">
                    <Input
                      type="time"
                      value={d.open}
                      aria-label={`${label} open`}
                      onChange={(e) => setDay(key, { open: e.target.value })}
                    />
                    <span className="faint">to</span>
                    <Input
                      type="time"
                      value={d.close}
                      aria-label={`${label} close`}
                      onChange={(e) => setDay(key, { close: e.target.value })}
                    />
                  </div>
                ) : (
                  <span className="faint">Closed</span>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <div className="settings-actions">
        {formError !== null && <div className="form-error">{formError}</div>}
        <Button variant="primary" loading={saving} onClick={() => void save()}>
          Save settings
        </Button>
      </div>
    </div>
  );
}

function initialDays(hours: BusinessHours): Record<Weekday, DayState> {
  const base: Record<Weekday, DayState> = {
    mon: { enabled: false, open: '09:00', close: '17:00' },
    tue: { enabled: false, open: '09:00', close: '17:00' },
    wed: { enabled: false, open: '09:00', close: '17:00' },
    thu: { enabled: false, open: '09:00', close: '17:00' },
    fri: { enabled: false, open: '09:00', close: '17:00' },
    sat: { enabled: false, open: '10:00', close: '16:00' },
    sun: { enabled: false, open: '10:00', close: '16:00' },
  };
  for (const d of hours.days) {
    base[d.day] = { enabled: true, open: d.open, close: d.close };
  }
  return base;
}
